import { TextField, styled } from '@mui/material';
import {
  DatePicker,
  LocalizationProvider,
  PickersDay,
  pickersDayClasses,
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Footer from 'components/common/Footer/Footer';
import HeaderLogo from 'components/common/HeaderLogo/HeaderLogo';
import ExternalClientAccessInfo from 'components/common/ExternalClientAccessInfo/ExternalClientAccessInfo';
import NumberFormatCustom from 'components/common/NumberFormatCustom';
import { getDatPickerFormat } from 'config/marketConfig';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useValidator } from 'hooks/useValidator';
import actions from 'store/actions';
import { updateNA, updateExternalAccessNA } from 'store/na/thunk';
import { getCountry } from 'utils/country';
import { dateParse, dateToString, epochToISOFormat } from 'utils/date';
import styles from './step1.module.scss';
import { ReactComponent as CalendarIcon } from 'icons/calendar.svg';
import { validationConfig } from 'config/validationConfig';
import { useTranslation } from 'react-i18next';
import { locale } from 'utils/locales';
import { useStepsWizard } from 'hooks/useStepsWizard';
import searchParams from 'constants/searchParams';
import paths from 'constants/paths';

const CustomTextField = styled(TextField)({
  '& .MuiFormHelperText-root': {
    marginLeft: 0,
  },
});

export default function Step1() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const params = new URLSearchParams(window.location.search);
  const preview = params.get(searchParams.preview);
  const na = useSelector((state) => state.na);
  const isExternalAccess = useSelector((state) => state.general.isExternalAccessMode);
  const isReadOnlyMode = useSelector((state) => state.general.isReadOnlyMode);
  const externalAccessConfig = useSelector((state) => state.externalAccessConfig);
  const [leaseExpirationYear, setLeaseExpirationYear] = useState(
    na.leaseExpirationYear ? new Date(na.leaseExpirationYear) : null,
  );
  const [size, setSize] = useState(na.size);
  const [startingYear, setStartingYear] = useState(new Date(na.startingYear));
  const [leaseTermInYears, setLeaseTermInYears] = useState(na.leaseTermInYears);
  const { goToStep } = useStepsWizard({
    naId: na.id,
    preview,
    isExternalAccess,
    accessKey: externalAccessConfig.accessKey,
  });

  const [validables, validator] = useValidator(
    ['leaseExpirationYear', 'startingYear', 'leaseTermInYears', 'size'],
    () => {
      const message = {};

      if (leaseExpirationYear) {
        const lowerLeaseExpirationYear = validationConfig.lowerLeaseExpirationYear;
        const upperLeaseExpirationYear = validationConfig.upperLeaseExpirationYear;
        const upperLeaseExpirationDate = new Date().setFullYear(
          new Date().getFullYear() + upperLeaseExpirationYear,
        );
        if (isNaN(leaseExpirationYear)) {
          message.leaseExpirationYear = t('step1.dateError5');
        }
        if (leaseExpirationYear.setHours(0, 0, 0, 0) < lowerLeaseExpirationYear) {
          message.leaseExpirationYear = t('step1.dateError3');
        }
        if (leaseExpirationYear.setHours(0, 0, 0, 0) >= upperLeaseExpirationDate) {
          message.leaseExpirationYear = t('step1.dateError1', { number: upperLeaseExpirationYear });
        }
      }

      const maxUpperStartingYears = validationConfig.maxUpperStartingYears;
      const lowerStartingYear = validationConfig.minStartingYearDate.getFullYear();
      const upperStartingYear = new Date().getFullYear() + maxUpperStartingYears;
      if (
        dateParse(startingYear) === 0 ||
        startingYear.getFullYear() < lowerStartingYear ||
        startingYear.getFullYear() > upperStartingYear
      )
        message.startingYear = t('step1.dateError6', { number: maxUpperStartingYears });

      const minLeaseYears = validationConfig.minLeaseYears;
      const maxLeaseYears = validationConfig.maxLeaseYears;
      if (!leaseTermInYears || leaseTermInYears < minLeaseYears || leaseTermInYears > maxLeaseYears)
        message.leaseTermInYears = t('step1.dateError2', {
          min: minLeaseYears,
          max: maxLeaseYears,
        });

      if (size != null && (size < validationConfig.rsfMin || size > validationConfig.rsfMax))
        message.size = t('step1.dateError7', {
          max: validationConfig.rsfMax / 1.0e6,
          min: validationConfig.rsfMin,
        });

      return message;
    },
  );

  const updateDataStore = (step) => {
    if (!validator.validate()) return false;

    const dirty =
      !!leaseExpirationYear !== !!na.leaseExpirationYear ||
      dateToString(leaseExpirationYear) !== dateToString(na.leaseExpirationYear) ||
      size !== na.size ||
      epochToISOFormat(dateParse(startingYear)) !== epochToISOFormat(na.startingYear) ||
      leaseTermInYears !== na.leaseTermInYears;
    if (dirty) {
      dispatch(actions.na.updateCurrentSituation(dateParse(leaseExpirationYear), size));
      dispatch(actions.na.updateStartingYear(dateParse(startingYear)));
      dispatch(actions.na.updateLeaseTermInYears(Number(leaseTermInYears)));
    }

    if (!isReadOnlyMode) {
      dispatch(actions.na.updateLastStep(step));
      dispatch(isExternalAccess ? updateExternalAccessNA({ hasChanged: dirty }) : updateNA);
    }

    return true;
  };

  const handleNext = () => {
    if (updateDataStore(paths.steps.yourPeople)) goToStep({ step: paths.steps.yourPeople });
  };

  return (
    <>
      {isReadOnlyMode && !isExternalAccess && <ExternalClientAccessInfo />}
      <div className={styles.container}>
        <div className={styles.column1}>
          <HeaderLogo />
          <div className={styles.row2}>
            <div className={styles.current_situation_column}>
              <div className={styles.current_situation_column_current_situation}>
                {t('step1.title1')}
              </div>
              <div className={styles.current_situation_column_description}>
                {t('step1.description1')}
              </div>
              <div className={styles.current_situation_column_field_title}>
                {t('step1.leaseExpDate')}
              </div>
              <LocalizationProvider
                adapterLocale={locale(getCountry())}
                dateAdapter={AdapterDateFns}>
                <DatePicker
                  disabled={isReadOnlyMode}
                  views={['year', 'month', 'day']}
                  className={styles.current_situation_column_lease_expiration_year}
                  inputFormat={getDatPickerFormat()}
                  renderDay={(_, __, pickersDayProps) => {
                    return (
                      <PickersDay
                        {...pickersDayProps}
                        sx={{
                          [`&&.${pickersDayClasses.selected}`]: {
                            backgroundColor: 'red',
                          },
                        }}
                      />
                    );
                  }}
                  inputProps={{
                    'data-testid': 'Step1DatePicker',
                    placeholder: t('step1.dateFormat'),
                  }}
                  value={leaseExpirationYear}
                  onChange={(newDate) => setLeaseExpirationYear(newDate ? newDate : null)}
                  components={{
                    OpenPickerIcon: CalendarIcon,
                  }}
                  renderInput={(params) => (
                    <CustomTextField
                      {...params}
                      error={!validables.leaseExpirationYear.valid}
                      helperText={validables.leaseExpirationYear.message}
                    />
                  )}
                />
              </LocalizationProvider>
              <div className={styles.current_situation_column_field_title}>{t('step1.size')}</div>
              <CustomTextField
                disabled={isReadOnlyMode}
                name="size_field"
                className={styles.current_situation_column_size}
                value={size}
                onChange={(event) => {
                  const value = Number(event.target.value);
                  setSize(value ? value : null);
                }}
                inputProps={{ 'data-testid': 'Step1SizeFieldText', min: 1, max: 1000000 }}
                InputProps={{
                  inputComponent: NumberFormatCustom,
                }}
                error={!validables.size.valid}
                helperText={validables.size.message}
              />
            </div>
            <div className={styles.your_upcoming_column}>
              <div className={styles.your_upcoming_column_currentsituation}>
                {t('step1.title2')}
              </div>
              <div className={styles.your_upcoming_column_description}>
                {t('step1.description2')}
              </div>
              <div className={styles.your_upcoming_column_field_title}>
                {t('step1.startingYear')}
              </div>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  disabled={isReadOnlyMode}
                  views={['year']}
                  className={styles.your_upcoming_column_starting_year}
                  minDate={validationConfig.minStartingYearDate}
                  value={startingYear}
                  onChange={setStartingYear}
                  inputProps={{
                    'data-testid': 'Step1DatePicker2',
                    placeholder: t('step1.yearFormat'),
                  }}
                  components={{
                    OpenPickerIcon: CalendarIcon,
                  }}
                  renderInput={(params) => (
                    <CustomTextField
                      {...params}
                      error={!validables.startingYear.valid}
                      helperText={validables.startingYear.message}
                    />
                  )}
                />
              </LocalizationProvider>
              <div className={styles.your_upcoming_column_field_title}>{t('step1.leaseTerm')}</div>
              <CustomTextField
                disabled={isReadOnlyMode}
                name="lease_term_in_years_field"
                className={styles.your_upcoming_column_lease_term_in_years}
                value={leaseTermInYears}
                onChange={(event) => setLeaseTermInYears(event.target.value)}
                onBlur={() => leaseTermInYears < 1 && setLeaseTermInYears(0)}
                onFocus={() => leaseTermInYears < 1 && setLeaseTermInYears('')}
                inputProps={{ 'data-testid': 'Step1SLeaseTermYearsText' }}
                InputProps={{
                  inputComponent: NumberFormatCustom,
                }}
                error={!validables.leaseTermInYears.valid}
                helperText={validables.leaseTermInYears.message}
              />
            </div>
          </div>
          <div className={styles.row3}>
            <Footer
              title={t('step1.footerTitle')}
              description={t('step1.footerDescription')}
              stepsHighlighted="1"
              handleback={null}
              handlenext={handleNext}></Footer>
          </div>
        </div>
        <div className={styles.column2}></div>
      </div>
    </>
  );
}
